import { HttpClient } from '@adapters/HttpClient';
import { ReservedOrderApiResponse } from '@modules/Products/List/models/ReservedOrder';
import { ProductsModel } from '../models/Products';

// Helper: serializa advanced_filters igual que en GET para mantener consistencia
function buildAdvancedFiltersParams(searchFilter: {
  filters: { column: string; operator: string; value?: any }[];
  logic: string;
}) {
  const params: Record<string, any> = {};
  if (searchFilter.filters && Array.isArray(searchFilter.filters)) {
    searchFilter.filters.forEach((f, i) => {
      params[`advanced_filters[filters][${i}][column]`] = f.column;
      params[`advanced_filters[filters][${i}][operator]`] = f.operator;
      // Solo enviar value si existe (para operadores is_null / is_not_null no debe enviarse)
      if (
        f.value !== undefined &&
        f.value !== null &&
        !(Array.isArray(f.value) && f.value.length === 0)
      ) {
        params[`advanced_filters[filters][${i}][value]`] = f.value;
      }
    });
  }
  if (searchFilter.logic) {
    params['advanced_filters[logic]'] = searchFilter.logic;
  }
  return params;
}

export interface ProductsFilters {
  searchTerm?: string;
  type_ids?: number[];
  product_ids?: number[];
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  page?: number;
  limit?: number;
  event_ids?: number[];
  columns?: string[];
}

export interface ProductsPage {
  products: ProductsModel[];
  nextPage?: number;
  hasMore: boolean;
  total: number;
}

export class ProductsApiAdapter {
  constructor(private httpClient: HttpClient) { }

  async getProductsPaginated(
    filters: ProductsFilters & {
      advanced_filters?: {
        filters: { column: string; operator: string; value: string }[];
        logic: string;
      };
    } = {}
  ): Promise<ProductsPage> {
    const {
      searchTerm = '',
      sortBy = 'id',
      sortDirection = 'asc',
      page = 1,
      limit = 25,
      advanced_filters,
    } = filters;

    const params: any = {
      ...(searchTerm && { search: searchTerm }),
      ...(sortBy && { order_by: sortBy }),
      ...(sortDirection && { order_direction: sortDirection }),
      page,
      per_page: limit,
    };

    // Serializar advanced_filters como query params anidados
    if (advanced_filters && Array.isArray(advanced_filters.filters)) {
      advanced_filters.filters.forEach((f, i) => {
        params[`advanced_filters[filters][${i}][column]`] = f.column;
        params[`advanced_filters[filters][${i}][operator]`] = f.operator;
        params[`advanced_filters[filters][${i}][value]`] = f.value;
      });
      params['advanced_filters[logic]'] = advanced_filters.logic;
    }

    // Solo mostrar loading global para la primera página
    const showGlobalLoading = page === 1;
    const response = await this.httpClient.get<any>(
      '/products',
      params,
      {},
      showGlobalLoading
    );

    // Asegura que products sea siempre un array, extrayendo de response.data
    const products = Array.isArray(response?.data) ? response.data : [];
    const meta = response?.meta ?? { currentPage: 1, lastPage: 1, total: 0 };

    return {
      products,
      nextPage:
        meta.currentPage < meta.lastPage
          ? meta.currentPage + 1
          : undefined,
      hasMore: meta.currentPage < meta.lastPage,
      total: meta.total ?? 0,
    };
  }

  async getAll(): Promise<ProductsModel[]> {
    const response = await this.getProductsPaginated();
    return response.products;
  }

  async exportProductsExcel(
    filters: ProductsFilters & {
      advanced_filters?: {
        filters: { column: string; operator: string; value?: any }[];
        logic: string;
      };
    } = {}
  ): Promise<string> {
    // Construir el body del POST exactamente como el GET (mismos defaults y keys)
    const params: Record<string, any> = {};

    if (filters.searchTerm) {
      params.search = filters.searchTerm;
    }

    const sortBy = filters.sortBy || 'id';
    const sortDirection = filters.sortDirection || 'asc';
    params.order_by = sortBy;
    params.order_direction = sortDirection;

    if (filters.advanced_filters) {
      Object.assign(
        params,
        buildAdvancedFiltersParams(filters.advanced_filters)
      );
    }

    // Incluir columnas visibles seleccionadas (obligatorio para exportar)
    if (filters.columns && Array.isArray(filters.columns)) {
      params.columns = filters.columns;
    }

    // Enviar parámetros en el body (no en querystring)
    const response = await this.httpClient.post<any>(
      '/products/export',
      params
    );
    return (
      response.url_file ||
      response.url ||
      response.data?.url_file ||
      response.data?.url
    );
  }

  async deleteProduct(productId: number): Promise<void> {
    await this.httpClient.delete(`/products/${productId}`);
  }

  /**
   * Obtiene las órdenes reservadas para un producto específico.
   * @param productId ID del producto
   * @param statusIds Array de IDs de estado (por defecto [3,4,5])
   * @param page Número de página (opcional)
   * @param perPage Cantidad de resultados por página (opcional)
   */
  async getReservedOrdersByProduct({
    productId,
    statusIds = [3, 4, 5],
    page = 1,
    perPage = 100,
  }: {
    productId: number;
    statusIds?: number[];
    page?: number;
    perPage?: number;
  }): Promise<ReservedOrderApiResponse> {
    const params: Record<string, any> = {
      product_ids: productId,
      status_ids: statusIds.join(','),
      page,
      per_page: perPage,
    };
    return this.httpClient.get<ReservedOrderApiResponse>('/orders', params);
  }

  async uploadProductsBulk(
    file: File
  ): Promise<{ message?: string; [key: string]: any }> {
    const formData = new FormData();
    formData.append('file', file);

    return this.httpClient.post(
      '/products/import/file',
      formData,
      true,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
  }
}
